import { Flex, Result, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { currentIntlLocale } from '@/app/i18n'
import type { ActivatedPackage } from '@/features/portal/accountVoucher'
import { formatBytes, formatDuration } from '@/shared/lib/format'

interface AccountActivatedStepProps {
  login: string
  activated: ActivatedPackage
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(currentIntlLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

export function AccountActivatedStep({ login, activated }: AccountActivatedStepProps) {
  const { t } = useTranslation()
  const { planName, expiresAt, quotaBytes, quotaSeconds } = activated
  const unlimited = quotaBytes === null && quotaSeconds === null

  return (
    <Result
      status="success"
      title={t('account.activatedTitle')}
      subTitle={t('account.activatedText', { login: `\u2068${login}\u2069` })}
      extra={
        <Flex vertical gap={2} style={{ textAlign: 'center' }}>
          {planName ? <Typography.Text strong>{t('account.plan', { name: planName })}</Typography.Text> : null}
          {unlimited ? <Typography.Text type="secondary">{t('success.unlimited')}</Typography.Text> : null}
          {quotaBytes !== null ? (
            <Typography.Text type="secondary">
              {t('success.quotaBytes', { amount: formatBytes(quotaBytes) })}
            </Typography.Text>
          ) : null}
          {quotaSeconds !== null ? (
            <Typography.Text type="secondary">
              {t('success.quotaSeconds', { amount: formatDuration(quotaSeconds) })}
            </Typography.Text>
          ) : null}
          {expiresAt ? (
            <Typography.Text type="secondary">
              {t('account.expires', { date: formatDateTime(expiresAt) })}
            </Typography.Text>
          ) : null}
        </Flex>
      }
    />
  )
}
